import { useState } from 'react';
import { ApiError } from '@/api/client';
import {
  downloadInferenceExtraction,
  saveBlob,
  type InferenceExtractionFilters,
} from '@/api/endpoints/exports';
import { useTranslation } from '@/i18n/useTranslation';

function localTimestamp(day: string, time: string): Date | null {
  if (!day || !time) return null;
  const timestamp = new Date(`${day}T${time}:00`);
  // Date normalizes nonexistent local times during daylight-saving transitions.
  if (
    Number.isNaN(timestamp.getTime()) ||
    timestamp.getFullYear() !== Number(day.slice(0, 4)) ||
    timestamp.getMonth() + 1 !== Number(day.slice(5, 7)) ||
    timestamp.getDate() !== Number(day.slice(8, 10)) ||
    timestamp.getHours() !== Number(time.slice(0, 2)) ||
    timestamp.getMinutes() !== Number(time.slice(3, 5))
  ) {
    return null;
  }
  return timestamp;
}

export function InferenceExtractionPanel() {
  const { errorText, t } = useTranslation();
  const [day, setDay] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [model, setModel] = useState('');
  const [vote, setVote] = useState<NonNullable<InferenceExtractionFilters['vote']> | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const [completed, setCompleted] = useState(false);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const from = localTimestamp(day, startTime);
  const to = localTimestamp(day, endTime);
  const completeRange = Boolean(day && startTime && endTime);
  const invalidTime = completeRange && (!from || !to);
  const invalidRange = Boolean(from && to && from >= to);

  const download = async () => {
    if (busy || !from || !to || invalidRange) return;
    setBusy(true);
    setError(null);
    setCompleted(false);
    try {
      const blob = await downloadInferenceExtraction({
        date_from: from.toISOString(),
        date_to: to.toISOString(),
        model_version: model.trim() || undefined,
        vote: vote || undefined,
      });
      saveBlob(blob, 'inferences.zip');
      setCompleted(true);
    } catch (cause) {
      setError({ cause });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="glass-panel stack" aria-labelledby="inference-extraction-title">
      <h2 id="inference-extraction-title">{t('Inference extraction for retraining')}</h2>
      <p>
        {t(
          'Download a ZIP containing the original scanned images and result.json files enriched with user feedback and detection review history.',
        )}
      </p>
      <div className="cluster">
        <label className="field">
          {t('Extraction day')}
          <input
            type="date"
            className="input"
            value={day}
            required
            disabled={busy}
            onChange={(event) => setDay(event.target.value)}
          />
        </label>
        <label className="field">
          {t('Start time')}
          <input
            type="time"
            className="input"
            value={startTime}
            step={60}
            required
            disabled={busy}
            onChange={(event) => setStartTime(event.target.value)}
          />
        </label>
        <label className="field">
          {t('End time')}
          <input
            type="time"
            className="input"
            value={endTime}
            step={60}
            required
            disabled={busy}
            onChange={(event) => setEndTime(event.target.value)}
          />
        </label>
        <label className="field">
          {t('Model version (optional)')}
          <input
            type="text"
            className="input"
            maxLength={128}
            value={model}
            disabled={busy}
            onChange={(event) => setModel(event.target.value)}
          />
        </label>
        <label className="field">
          {t('User feedback')}
          <select
            className="input"
            value={vote}
            disabled={busy}
            onChange={(event) => setVote(event.target.value as typeof vote)}
          >
            <option value="">{t('All feedback states')}</option>
            <option value="reviewed">{t('With user feedback')}</option>
            <option value="correct">{t('Correct')}</option>
            <option value="incorrect">{t('Incorrect')}</option>
            <option value="unreviewed">{t('Without user feedback')}</option>
          </select>
        </label>
      </div>
      <p>{t('Times use your browser timezone: {timezone}.', { timezone })}</p>
      <p>
        {t(
          'Select a day and both times. Inference completion times include the start and exclude the end.',
        )}
      </p>
      <p>
        {t(
          'Only retained, saved inferences are included. For large datasets, download smaller time windows.',
        )}
      </p>
      {invalidTime && (
        <p role="alert">{t('The selected time does not exist in your browser timezone.')}</p>
      )}
      {invalidRange && <p role="alert">{t('The end time must be later than the start time.')}</p>}
      <div>
        <button
          type="button"
          className="btn btn--accent"
          disabled={busy || !completeRange || invalidTime || invalidRange}
          onClick={() => void download()}
        >
          {busy ? t('Preparing inference ZIP…') : t('Download inference ZIP')}
        </button>
      </div>
      {completed && <p role="status">{t('Inference ZIP prepared. Your download has started.')}</p>}
      {error && (
        <p role="alert" className="error-state">
          {error.cause instanceof Error
            ? errorText(
                error.cause.message,
                error.cause instanceof ApiError ? error.cause.status : undefined,
              )
            : t('Could not extract inferences.')}
        </p>
      )}
    </section>
  );
}
